import React, { forwardRef, useEffect, useState } from 'react';
import { useReactiveVar } from '@apollo/client';

import { useStyles } from './ecp-stores.styles';
import { EcpStore } from '../ecp-store';
import { IEcpStore } from '../../../../rest/ecp-locator';
import { IEcpStoresProps } from './ecp-stores.models';
import {
  sortingType,
  pinnedStoreCode,
  setMarkerCode,
} from '../../../../graphql/ecp-locator/ecp-locator.cache';
import { useSearchParams } from 'react-router-dom';
import { SortingTypes } from '../ecp-sort/ecp-sort.models';
import { sortByRaiting, sortByDistance } from '../../../../utils/ecp-locator';

const normalizeForMatch = (str: string): string =>
  str.toLowerCase().replace(/[^a-zа-яё0-9]/g, '');

const findMatchedStore = (stores: IEcpStore[], address: string): IEcpStore | undefined => {
  const normalized = normalizeForMatch(address);

  return stores.find((store) => {
    const storeNormalized = normalizeForMatch(store.street);
    return storeNormalized === normalized
      || storeNormalized.includes(normalized)
      || normalized.includes(storeNormalized);
  });
};

export const EcpStores = forwardRef<HTMLUListElement, IEcpStoresProps>(
  ({ stores, onStoreSelect, storeCode, onScrollToStore }, ref): JSX.Element => {
    const classes = useStyles();
    const currentSortingType = useReactiveVar(sortingType);
    const currentPinnedCode = useReactiveVar(pinnedStoreCode);
    const [searchParams] = useSearchParams();
    const urlAddress = searchParams.get('address');
    const [orderedStores, setOrderedStores] = useState<IEcpStore[]>([]);

    useEffect(() => {
      // Set "selected" state for a map marker
      if (urlAddress) {
        const matchedStore = findMatchedStore(stores || [], urlAddress || '');
        if (matchedStore) {
          pinnedStoreCode(matchedStore.code);
          setMarkerCode(matchedStore.code);
        }
      }
    }, [stores, urlAddress]);

    useEffect(() => {
      if (!stores) {
        setOrderedStores([]);
        return;
      }

      const isSortByRating = currentSortingType === SortingTypes.rating;
      const sortingCallback = isSortByRating ? sortByRaiting : sortByDistance;
      let sorted = sortingCallback([...stores]);

      if (currentPinnedCode) {
        const pinnedIndex = sorted.findIndex(({ code }) => code === currentPinnedCode);
        if (pinnedIndex > 0) {
          const [pinned] = sorted.splice(pinnedIndex, 1);
          sorted = [pinned, ...sorted];
        }
      }

      setOrderedStores(sorted);
    }, [stores, currentSortingType, currentPinnedCode]);

    return (
      <ul
        ref={ref}
        className={classes.root}
        role="list"
        data-testid="stores-list"
      >
        {orderedStores?.map((store: IEcpStore) => (
          <li
            key={store.code}
            role="listitem"
            className={classes.storeItem}
          >
            <EcpStore
              storeData={store}
              onStoreSelect={onStoreSelect}
              storeCode={storeCode}
              onScrollToStore={onScrollToStore}
              contentPartialStyles={classes.storeContentPartial}
            />
          </li>
        ))}
      </ul>
    );
  },
);
